import { useAppSelector, useAppDispatch } from "@/services/hooks";
// containers/EditPermissionContainer.tsx
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { saveUser, editPermission } from "@/services/userServices/userActions";
import { COUNTRY_OPTIONS, type SelectOption } from "@/utils/selectOptions";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/services/api";
import EditPermissionView from "./EditPermissionView";
import NotificationAlertOptions from "@/layouts/Alerts";

/** One per-site permission row; only its country code is read here. */
type PermissionRow = { CountryCode: string };

/** GET /api/users/:id as this screen reads it. */
type EditedUser = {
  _id?: string;
  name?: string;
  email?: string;
  role?: string;
  enabled?: boolean;
  avatar?: string;
  countrycode?: SelectOption[];
  permission?: PermissionRow[];
};

export default function EditPermissionContainer() {
  const dispatch = useAppDispatch();
  const { id } = useParams();
  const [user, setUser] = useState<EditedUser>({});
  const [usertoUpdate, setUsertoUpdate] = useState<EditedUser>({});
  const [countrycode, setCountrycode] = useState<SelectOption[]>([]);
  // SCRUM-1887 (same defect as SCRUM-1885): redirect with navigate(), not a
  // `redirect` state rendered as <Navigate>. An instance that survived a quick
  // browser Back still held that state and rendered nothing.
  const navigate = useNavigate();
  const registerUser = useAppSelector(s => s.users.register_user); // if you still need it

  
  const storeUserTypeInformation = useCallback((nextCountrycode: SelectOption[], baseUser: EditedUser) => {
    const selectedCodes = (nextCountrycode || []).map(c => c.value);
    const onlyPerms = (baseUser.permission || []).filter(p =>
      selectedCodes.includes(p.CountryCode)
    );
    dispatch(saveUser({
      name: baseUser.name,
      email: baseUser.email,
      countrycode: nextCountrycode,
      role: baseUser.role,
      enabled: baseUser.enabled,
      avatar: baseUser.avatar,
      permission: onlyPerms,
    }));
  }, [dispatch]);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/api/users/${id}`);
        setUser(res.data);
        setUsertoUpdate(res.data);
        setCountrycode(res.data.countrycode || []);
        storeUserTypeInformation(res.data.countrycode || [], res.data);
      } catch {
        // notfound UI stays in the view; we just redirect if you want
      }
    })();
  }, [id, storeUserTypeInformation]);

  const onSelectCountries = (selected: SelectOption[] | null) => {
    setCountrycode(selected || []);
    storeUserTypeInformation(selected || [], user);
  };

  const onFinish = async (e: React.FormEvent) => {
    e.preventDefault();
    // Only redirect when the permission update actually succeeded. editPermission now
    // re-throws on failure (SCRUM-769), so a server-side error no longer falls through
    // to a silent navigation to /admin/users as if the update had been saved — and a
    // failed update no longer corrupts the user slice via UPDATE_USER.
    try {
      // SCRUM-1033: editPermission is a createAppAsyncThunk now, so .unwrap() is
      // what turns a failed PUT back into a rejection — without it `await
      // dispatch(...)` resolves even on failure and the SCRUM-769 silent redirect
      // returns.
      await dispatch(editPermission({
        _id: usertoUpdate._id,
        countrycode,
        permission: (registerUser?.permission ?? user.permission ?? []),
      })).unwrap();
      navigate("/admin/users");
    } catch (err) {
      // Errors are surfaced by the shell hub (GlobalNotifications) from state.errors;
      // editPermission is a createAppAsyncThunk, so appErrorMiddleware already feeds
      // its displayable payload there. The local toast here rendered NOWHERE —
      // EditPermissionView mounts no default ToastContainer — so this fallback copy was
      // dead, and react-toastify could queue it until a later route mounted one
      // (SCRUM-1039). The catch stays so a rejected save does not redirect.
    }
  };

  return (
    <EditPermissionView
      userHeader={usertoUpdate}
      countrycode={countrycode}
      countriesOptions={COUNTRY_OPTIONS}
      onSelectCountries={onSelectCountries}
      onFinish={onFinish}
    />
  );
}
